import fillInGapTimespan from "components/pages/schedule/helpers/fillInGapTimespan";
import OverlappingPlannedBreaks from "components/pages/schedule/helpers/OverlappingPlannedBreaks";
import cloneDeep from "lodash/cloneDeep";
import DateTime from "utils/DateTime";
import WorkBlockFactory from "./WorkBlockFactory";
import WorkOrder from "./WorkOrder";

const SECONDS_IN_HOUR = 3600;

const ITEM_DESCRIPTION_LAYOUT = "item_description";
const ITEM_CODE_LAYOUT = "item_code";
const WORK_ORDER_DESCRIPTION_LAYOUT = "work_order_description";
const WORK_ORDER_ID_LAYOUT = "work_order_id";
const CUSTOMER_LAYOUT = "customer";

const LAYOUTS = [
  { label: "Item description", value: ITEM_DESCRIPTION_LAYOUT },
  { label: "Item code", value: ITEM_CODE_LAYOUT },
  { label: "Work order description", value: WORK_ORDER_DESCRIPTION_LAYOUT },
  { label: "Work order ID", value: WORK_ORDER_ID_LAYOUT },
  { label: "Customer", value: CUSTOMER_LAYOUT },
];

const WorkBlock = {
  addWorkTimeDuration,
  buildWithBlockTime: WorkBlockFactory.buildWithBlockTime,
  buildWithWorkTime: WorkBlockFactory.buildWithWorkTime,
  CUSTOMER_LAYOUT,
  calculateBlockDurationForLine,
  description,
  effectiveProductionRate,
  efficiency,
  ITEM_CODE_LAYOUT,
  ITEM_DESCRIPTION_LAYOUT,
  isPastDue,
  LAYOUTS,
  laborRatio,
  lineEfficiency,
  overlappingPlannedBreaks: WorkBlockFactory.overlappingPlannedBreaks,
  overlappingPlannedBreaksDuration: WorkBlockFactory.overlappingPlannedBreaksDuration,
  pendingScheduledQuantity,
  quantity,
  standardProductionRate,
  updateBlock: WorkBlockFactory.updateBlock,
  updateBlockWithGapFiller,
  validateInput,
  WORK_ORDER_DESCRIPTION_LAYOUT,
  WORK_ORDER_ID_LAYOUT,
  workTime: WorkBlockFactory.workTime,
  workTimeDuration,
};

function isPastDue(workBlock) {
  const cancelled = WorkOrder.isCancelled(workBlock.workOrder);
  const completed = WorkOrder.isCompleted(workBlock.workOrder);
  const pastDue = workBlock.endsAt > DateTime.endOfDay(workBlock.workOrder.due).format();

  return !cancelled && !completed && pastDue;
}

// @keep_effective_production_rate_calculations_in_sync
function effectiveProductionRate(workBlock) {
  return standardProductionRate(workBlock) * efficiency(workBlock) * laborRatio(workBlock) * performance(workBlock);
}

function performance(workBlock) {
  if (workBlock?.performanceOverride > 0) {
    return workBlock.performanceOverride;
  }
  return workBlock.performance;
}

function standardProductionRate(workBlock) {
  if (workBlock?.productionRateOverride > 0) {
    return workBlock.productionRateOverride;
  }
  return workBlock.standardProductionRate;
}

function efficiency(workBlock) {
  return workBlock.efficiencyOverride || lineEfficiency(workBlock);
}

function lineEfficiency(workBlock) {
  if (workBlock.lineEfficiency) {
    return workBlock.lineEfficiency;
  }
  return workBlock.line?.efficiency;
}

function laborRatio(workBlock) {
  if (hasLaborOverride(workBlock)) {
    return workBlock.laborOverride / workBlock.standardPeople;
  }
  return 1;
}

function hasLaborOverride(workBlock) {
  return workBlock.productionRateDependsOnNumberOfPeople && workBlock.laborOverride && workBlock.standardPeople;
}

function description({ workOrder }, workBlockLayout) {
  return {
    [ITEM_DESCRIPTION_LAYOUT]: workOrder.itemDescription,
    [ITEM_CODE_LAYOUT]: workOrder.itemCode,
    [WORK_ORDER_DESCRIPTION_LAYOUT]: workOrder.description,
    [WORK_ORDER_ID_LAYOUT]: workOrder.externalId ? `${workOrder.externalId}` : null,
    [CUSTOMER_LAYOUT]: workOrder.customerName,
  }[workBlockLayout];
}

function quantity(workBlock) {
  const productiveDurationInHours =
    WorkBlockFactory.workTime(workBlock).durationInHours() - plannedBreakDurationInHours(workBlock);

  const calculatedQuantity = WorkBlock.effectiveProductionRate(workBlock) * productiveDurationInHours;

  return clampQuantity(workBlock, calculatedQuantity);
}

function pendingScheduledQuantity(workBlock) {
  const { productionLastUpdatedAt: rawProductionLastUpdatedAt } = workBlock.workOrder;
  const productionLastUpdatedDate = rawProductionLastUpdatedAt ? new Date(rawProductionLastUpdatedAt) : null;

  const workBlockDurationInHours = WorkBlockFactory.workTime(workBlock)
    .shrinkStartTo(productionLastUpdatedDate)
    .durationInHours();
  const breaks = new OverlappingPlannedBreaks({
    line: workBlock.line,
  });

  const overlappingPlannedBreaksDurationInHours =
    breaks
      .getCombinedOverlappingPlannedBreaks(
        WorkBlockFactory.workTime(workBlock).shrinkStartTo(productionLastUpdatedDate),
      )
      .reduce((partialSum, item) => partialSum + item.duration, 0.0) / SECONDS_IN_HOUR;

  const calculatedQuantity =
    WorkBlock.effectiveProductionRate(workBlock) * (workBlockDurationInHours - overlappingPlannedBreaksDurationInHours);

  return clampQuantity(workBlock, calculatedQuantity);
}

function clampQuantity(workBlock, calculatedQuantity) {
  const rawQuantity = workBlock.quantity;
  if (!rawQuantity) return calculatedQuantity;

  const difference = Math.abs(calculatedQuantity - rawQuantity);
  const percentageDifference = difference / rawQuantity;

  return percentageDifference < 0.0001 ? rawQuantity : calculatedQuantity;
}

function plannedBreakDurationInHours(workBlock) {
  const breakDurationInSeconds = workBlock.overlappingPlannedBreaks
    ? workBlock.overlappingPlannedBreaks.reduce((partialSum, item) => partialSum + item.duration, 0.0)
    : 0.0;

  return breakDurationInSeconds / SECONDS_IN_HOUR;
}

function calculateBlockDurationForLine(workBlock, line) {
  const { teardownTimeDuration } = workBlock;
  const composedWorkBlock = {
    ...workBlock,
    line,
    lineEfficiency: line.efficiency,
  };

  const rate = WorkBlock.effectiveProductionRate(composedWorkBlock);

  return teardownTimeDuration + (workBlock.quantity / rate) * SECONDS_IN_HOUR;
}

function workTimeDuration(workBlock) {
  return WorkBlockFactory.workTime(workBlock).durationInSeconds();
}

function addWorkTimeDuration(workBlock, duration) {
  const { workTimeStartsAt, workTimeEndsAt } = workBlock;

  return WorkBlock.updateBlock(workBlock, {
    workTimeEndsAt: DateTime.addDuration(workTimeEndsAt, duration, "seconds"),
    workTimeStartsAt,
  });
}

function updateBlockWithGapFiller({ block, startDate, endDate, line, gapFiller }) {
  let updatedBlock = cloneDeep(block);

  updatedBlock = WorkBlock.updateBlock(updatedBlock, {
    endsAt: DateTime.toISO8601(endDate),
    line,
    startsAt: DateTime.toISO8601(startDate),
  });
  const isInFuture = DateTime.isInTheFuture(startDate);

  if (line) {
    const timespanWithinGap = fillInGapTimespan({
      block,
      gapFiller,
      line,
    });

    if (timespanWithinGap) {
      updatedBlock = WorkBlock.updateBlock(updatedBlock, {
        endsAt: DateTime.toISO8601(timespanWithinGap.endTime),
        line,
        startsAt: DateTime.toISO8601(timespanWithinGap.startTime),
      });
    }
  }

  if (isInFuture) {
    // line is either the work block's line or a new line
    // for work blocks in the past, the line efficiency is cached and does not need to be updated
    // for work blocks in the future, the line efficiency needs to be updated to reflect the current line efficiency
    updatedBlock.lineEfficiency = line.efficiency;
  }

  return updatedBlock;
}

function validateInput(data) {
  const errors = {};

  if (new Date(data.workTimeStartsAt) >= new Date(data.workTimeEndsAt)) {
    errors.workTimeStartsAt = "Start time must be before end time";
    errors.workTimeEndsAt = "End time must be after start time";
  }

  const {
    productionRateOverride,
    performanceOverride,
    efficiencyOverride,
    laborOverride,
    teardownTimeDurationHours,
    teardownTimeLabel,
  } = data;

  if (productionRateOverride !== null && productionRateOverride <= 0 && productionRateOverride !== "") {
    errors.productionRateOverride = "Production rate must be greater than 0";
  }

  if (performanceOverride !== null && performanceOverride <= 0 && performanceOverride !== "") {
    errors.performanceOverride = "Performance must be greater than 0";
  }

  if (efficiencyOverride !== null && efficiencyOverride <= 0 && efficiencyOverride !== "") {
    errors.efficiencyOverride = "Line efficiency must be greater than 0";
  }

  if (laborOverride !== null && laborOverride <= 0 && laborOverride !== "") {
    errors.laborOverride = "Labor must be greater than 0";
  }

  if (teardownTimeDurationHours !== null && teardownTimeDurationHours < 0 && teardownTimeDurationHours !== "") {
    errors.teardownTimeDurationHours = "Duration must be greater than or equal to 0";
  }

  if (!teardownTimeLabel || teardownTimeLabel.trim() === "") {
    errors.teardownTimeLabel = "Label is required";
  }

  return errors;
}

export default WorkBlock;
